import { useEffect, useState } from "react";
import { Building2, Plus, Upload, Link2, Trash2, PenSquare, FileSpreadsheet, Loader2, Sparkles, Download } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, errMsg, getToken } from "@/lib/api";
import { fmtRp, fmtRpShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SectionCard, Segmented, InfoNote } from "@/components/common/Cards";
import { Field, MoneyInput, NumInput } from "@/components/common/Inputs";
import { DEFAULT_PROFILE, ENTITY_LABEL, REGIME_LABEL, SECTION_LABEL, plTotals, rid } from "./eqUtils";

export async function downloadTemplate() {
  try {
    const resp = await fetch(`${API_BASE}/equalizer/template`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "template-ekualizer-pajak.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(errMsg(e, "Gagal mengunduh template"));
  }
}

export function CompanyPanel({ companies, loading, meta, onReload }) {
  const [editing, setEditing] = useState(null); // company object or {new:true}
  const [importing, setImporting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const loadSample = async () => {
    setSeeding(true);
    try {
      await api.post("/equalizer/companies/sample");
      toast.success("2 PT contoh ditambahkan.");
      onReload();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menambah data contoh"));
    } finally {
      setSeeding(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/equalizer/companies/${deleting.id}`);
      toast.success("PT dihapus.");
      setDeleting(null);
      onReload();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menghapus"));
    }
  };

  return (
    <div className="space-y-5">
      <SectionCard
        title="Data PT"
        description="Tambah PT beserta profil pajaknya, lalu impor Laba Rugi & Neraca dari Google Sheet atau Excel/CSV."
        testid="eq-companies-card"
        actions={
          <>
            <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="eq-download-template">
              <Download className="h-4 w-4 mr-1.5" /> Template
            </Button>
            <Button size="sm" variant="outline" onClick={loadSample} disabled={seeding} data-testid="eq-load-sample">
              {seeding ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Data contoh
            </Button>
            <Button size="sm" className="press" onClick={() => setEditing({ isNew: true })} data-testid="eq-add-company">
              <Plus className="h-4 w-4 mr-1.5" /> Tambah PT
            </Button>
          </>
        }
      >
        {loading ? (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-44 bg-[hsl(220_10%_10%)]" />
            ))}
          </div>
        ) : companies.length === 0 ? (
          <div className="rounded-xl border border-border bg-[hsl(220_10%_7%)] p-8 text-center text-sm text-muted-foreground" data-testid="eq-companies-empty">
            <Building2 className="h-6 w-6 mx-auto mb-2 text-primary/70" />
            Belum ada PT. Klik <b className="text-foreground">Tambah PT</b> atau <b className="text-foreground">Data contoh</b> untuk mencoba.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="eq-companies-grid">
            {companies.map((c) => {
              const t = plTotals(c.items);
              const p = { ...DEFAULT_PROFILE, ...(c.profile || {}) };
              const counts = (c.items || []).reduce((acc, i) => ({ ...acc, [i.section]: (acc[i.section] || 0) + 1 }), {});
              return (
                <div key={c.id} className="rounded-xl border border-border bg-card p-4 elev-1 flex flex-col" data-testid={`eq-company-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display text-lg leading-tight truncate">{c.name}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {ENTITY_LABEL[p.entity_type]} {c.npwp ? `· NPWP ${c.npwp}` : ""} {c.period ? `· Periode ${c.period}` : ""}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(c)} data-testid={`eq-company-${c.id}-edit`} aria-label="Edit">
                        <PenSquare className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDeleting(c)} data-testid={`eq-company-${c.id}-delete`} aria-label="Hapus">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <Badge variant="outline" className="border-primary/40 text-primary text-[10px]">{REGIME_LABEL[p.regime]}</Badge>
                    {p.regime === "normal" && p.fasilitas_31e && <Badge variant="outline" className="text-[10px] border-border">Pasal 31E</Badge>}
                    <Badge variant="outline" className="text-[10px] border-border">{p.is_pkp ? "PKP" : "Non-PKP"}</Badge>
                    {Number(p.loss_carryforward) > 0 && (
                      <Badge variant="outline" className="text-[10px] border-border">Rugi fiskal {fmtRpShort(p.loss_carryforward)}</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                    <div className="rounded-lg border border-[hsl(160_45%_25%)] bg-[hsl(160_40%_8%)] p-2">
                      <div className="text-[10px] text-[hsl(160_40%_70%)]">Pendapatan</div>
                      <div className="num text-[hsl(160_55%_62%)] mt-0.5">{fmtRpShort(t.rev)}</div>
                    </div>
                    <div className="rounded-lg border border-[hsl(0_45%_30%)] bg-[hsl(0_40%_9%)] p-2">
                      <div className="text-[10px] text-[hsl(0_50%_75%)]">Beban</div>
                      <div className="num text-[hsl(0_72%_68%)] mt-0.5">{fmtRpShort(t.exp)}</div>
                    </div>
                    <div className="rounded-lg border border-border bg-[hsl(220_10%_8%)] p-2">
                      <div className="text-[10px] text-muted-foreground">Laba</div>
                      <div className={`num mt-0.5 ${t.laba < 0 ? "text-[hsl(0_72%_68%)]" : "text-primary"}`}>{fmtRpShort(t.laba)}</div>
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-3 flex-1">
                    {(c.items || []).length === 0
                      ? "Belum ada data keuangan."
                      : `${counts.pendapatan || 0} pendapatan · ${counts.beban || 0} beban · ${(counts.aset || 0) + (counts.liabilitas || 0) + (counts.ekuitas || 0)} pos neraca`}
                    {c.source?.type && c.source.type !== "manual" && (
                      <span className="block mt-0.5">Sumber: {c.source.type === "google_sheet" ? "Google Sheet" : c.source.type === "sample" ? "Data contoh" : c.source.filename}</span>
                    )}
                  </div>
                  <Button size="sm" variant="outline" className="mt-3" onClick={() => setImporting(c)} data-testid={`eq-company-${c.id}-import`}>
                    <Upload className="h-4 w-4 mr-1.5" /> Impor Neraca & Laba Rugi
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <CompanyDialog key={editing?.id || (editing ? "new" : "none")} company={editing} meta={meta} onClose={() => setEditing(null)} onSaved={onReload} />
      <ImportDialog company={importing} onClose={() => setImporting(null)} onDone={onReload} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Data PT dan pos keuangannya akan dihapus permanen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="eq-company-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="eq-company-delete-confirm">Ya, hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CompanyDialog({ company, meta, onClose, onSaved }) {
  const open = !!company;
  const isNew = !!company?.isNew;
  const [form, setForm] = useState(() => ({
    name: company?.name || "",
    npwp: company?.npwp || "",
    period: company?.period || String(new Date().getFullYear() - 1),
    notes: company?.notes || "",
    profile: { ...DEFAULT_PROFILE, ...(company?.profile || {}) },
    items: (company?.items || []).map((i) => ({ ...i })),
  }));
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState("pendapatan");
  const setP = (patch) => setForm((f) => ({ ...f, profile: { ...f.profile, ...patch } }));
  const p = form.profile;

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nama PT wajib diisi.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        profile: { ...p, registered_year: p.registered_year ? Number(p.registered_year) : null },
        items: form.items.filter((i) => i.name.trim()).map((i) => ({ ...i, amount: Number(i.amount) || 0 })),
      };
      if (isNew) await api.post("/equalizer/companies", body);
      else await api.put(`/equalizer/companies/${company.id}`, body);
      toast.success(isNew ? "PT ditambahkan." : "PT diperbarui.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menyimpan PT"));
    } finally {
      setSaving(false);
    }
  };

  const fiscalOpts = section === "beban" ? meta?.expense_fiscal || [] : section === "pendapatan" ? meta?.revenue_fiscal || [] : [];
  const sectionItems = form.items.map((it, idx) => ({ it, idx })).filter(({ it }) => it.section === section);
  const updItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((x, i) => (i === idx ? { ...x, ...patch } : x)) }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="eq-company-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Tambah PT" : `Edit ${company?.name}`}</DialogTitle>
          <DialogDescription>Profil pajak menentukan cara PPh Badan, PPN, dan PPh 23 dihitung pada simulasi.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="profil">
          <TabsList className="bg-[hsl(220_12%_8%)] border border-border">
            <TabsTrigger value="profil" data-testid="eq-company-tab-profile">Profil pajak</TabsTrigger>
            <TabsTrigger value="pos" data-testid="eq-company-tab-items">Pos keuangan ({form.items.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="profil" className="space-y-4 mt-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Nama PT">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="PT Contoh Jaya" className="h-10 bg-[hsl(220_12%_8%)]" data-testid="eq-company-name" />
              </Field>
              <Field label="NPWP (opsional)">
                <Input value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} className="h-10 bg-[hsl(220_12%_8%)]" data-testid="eq-company-npwp" />
              </Field>
              <Field label="Tahun pajak">
                <Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className="h-10 bg-[hsl(220_12%_8%)]" data-testid="eq-company-period" />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Bentuk badan">
                <Select value={p.entity_type} onValueChange={(v) => setP({ entity_type: v })}>
                  <SelectTrigger className="h-10 bg-[hsl(220_12%_8%)]" data-testid="eq-company-entity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ENTITY_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Skema PPh Badan">
                <Segmented
                  value={p.regime}
                  onChange={(v) => setP({ regime: v })}
                  testid="eq-company-regime"
                  options={[
                    { value: "normal", label: "Tarif umum 22%" },
                    { value: "umkm_final", label: "Final 0,5% (UMKM)" },
                  ]}
                />
              </Field>
            </div>
            {p.regime === "umkm_final" && p.entity_type === "pt" && (
              <InfoNote testid="eq-pp20-note">
                PP 20/2026: PT (bukan PT Perorangan) yang terdaftar sejak 22 April 2026 tidak bisa memakai PPh final 0,5%. PT yang terdaftar sebelumnya hanya boleh maksimal 3 tahun sejak terdaftar.
              </InfoNote>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <ToggleRow label="Fasilitas Pasal 31E" hint="Diskon 50% tarif untuk omzet ≤ Rp50 M" checked={p.fasilitas_31e} disabled={p.regime !== "normal"} onChange={(v) => setP({ fasilitas_31e: v })} testid="eq-company-31e" />
              <ToggleRow label="Pengusaha Kena Pajak (PKP)" hint="PKP memungut PPN & dapat mengkreditkan PPN masukan" checked={p.is_pkp} onChange={(v) => setP({ is_pkp: v })} testid="eq-company-pkp" />
              <ToggleRow label="DPP nilai lain 11/12" hint="PMK 131/2024: PPN 12% × 11/12 ≈ 11% efektif" checked={p.ppn_dpp_nilai_lain} onChange={(v) => setP({ ppn_dpp_nilai_lain: v })} testid="eq-company-dpp" />
              <Field label="Tahun terdaftar (opsional)">
                <Input value={p.registered_year || ""} onChange={(e) => setP({ registered_year: e.target.value.replace(/\D/g, "").slice(0, 4) })} placeholder="2022" className="h-10 bg-[hsl(220_12%_8%)]" data-testid="eq-company-registered" />
              </Field>
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
              <Field label="Tarif PPN">
                <NumInput value={p.ppn_rate} onChange={(v) => setP({ ppn_rate: v })} suffix="%" testid="eq-company-ppn" />
              </Field>
              <Field label="PPh 23 jasa">
                <NumInput value={p.pph23_rate} onChange={(v) => setP({ pph23_rate: v })} suffix="%" testid="eq-company-pph23" />
              </Field>
              <Field label="PPh 4(2) sewa">
                <NumInput value={p.pph42_sewa_rate} onChange={(v) => setP({ pph42_sewa_rate: v })} suffix="%" testid="eq-company-pph42" />
              </Field>
              <Field label="Sisa rugi fiskal">
                <MoneyInput value={p.loss_carryforward} onChange={(v) => setP({ loss_carryforward: v })} testid="eq-company-loss" />
              </Field>
            </div>
          </TabsContent>
          <TabsContent value="pos" className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Segmented
                value={section}
                onChange={setSection}
                size="sm"
                testid="eq-items-section"
                options={Object.entries(SECTION_LABEL).map(([k, v]) => ({ value: k, label: `${v} (${form.items.filter((i) => i.section === k).length})` }))}
              />
              <Button
                size="sm"
                variant="outline"
                className="ml-auto"
                onClick={() => setForm((f) => ({ ...f, items: [...f.items, { id: rid(), section, group: "", name: "", amount: 0, fiscal: "" }] }))}
                data-testid="eq-items-add"
              >
                <Plus className="h-4 w-4 mr-1" /> Tambah pos
              </Button>
            </div>
            {sectionItems.length === 0 ? (
              <div className="text-xs text-muted-foreground rounded-lg border border-border p-4 text-center">Belum ada pos {SECTION_LABEL[section].toLowerCase()}.</div>
            ) : (
              <div className="space-y-2">
                {sectionItems.map(({ it, idx }) => (
                  <div key={it.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`eq-item-row-${idx}`}>
                    <Input value={it.name} onChange={(e) => updItem(idx, { name: e.target.value })} placeholder="Nama akun" className="col-span-12 sm:col-span-4 h-9 bg-[hsl(220_12%_8%)]" data-testid={`eq-item-${idx}-name`} />
                    <div className="col-span-6 sm:col-span-3">
                      <MoneyInput value={it.amount} onChange={(v) => updItem(idx, { amount: v })} testid={`eq-item-${idx}-amount`} />
                    </div>
                    <div className="col-span-5 sm:col-span-4">
                      {fiscalOpts.length > 0 ? (
                        <Select value={it.fiscal || "__none"} onValueChange={(v) => updItem(idx, { fiscal: v === "__none" ? "" : v })}>
                          <SelectTrigger className="h-9 bg-[hsl(220_12%_8%)] text-xs" data-testid={`eq-item-${idx}-fiscal`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {fiscalOpts.map((o) => (
                              <SelectItem key={o.value || "__none"} value={o.value || "__none"}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">Pos neraca</span>
                      )}
                    </div>
                    <Button size="icon" variant="ghost" className="col-span-1 h-9 w-9 text-destructive hover:text-destructive" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} aria-label="Hapus pos" data-testid={`eq-item-${idx}-delete`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">Total pendapatan {fmtRp(plTotals(form.items).rev)} · beban {fmtRp(plTotals(form.items).exp)}</p>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="eq-company-cancel">Batal</Button>
          <Button onClick={save} disabled={saving} className="press" data-testid="eq-company-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Simpan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({ label, hint, checked, onChange, disabled, testid }) {
  return (
    <div className={`flex items-center justify-between gap-3 rounded-lg border border-border bg-[hsl(220_10%_7%)] px-3 py-2.5 ${disabled ? "opacity-60" : ""}`}>
      <div>
        <div className="text-sm">{label}</div>
        <div className="text-[11px] text-muted-foreground">{hint}</div>
      </div>
      <Switch checked={!!checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
    </div>
  );
}

function ImportDialog({ company, onClose, onDone }) {
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [replace, setReplace] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setUrl("");
    setFile(null);
    setReplace(true);
  }, [company]);

  const report = (data) => {
    const c = data.counts || {};
    toast.success(`Impor berhasil: ${data.imported} pos (${c.pendapatan || 0} pendapatan, ${c.beban || 0} beban, ${(c.aset || 0) + (c.liabilitas || 0) + (c.ekuitas || 0)} neraca).`);
    onDone();
    onClose();
  };
  const fromSheet = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/equalizer/companies/${company.id}/import-sheet`, { url, replace });
      report(data);
    } catch (e) {
      toast.error(errMsg(e, "Gagal impor Google Sheet"));
    } finally {
      setBusy(false);
    }
  };
  const fromFile = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/equalizer/companies/${company.id}/import?replace=${replace}`, fd);
      report(data);
    } catch (e) {
      toast.error(errMsg(e, "Gagal impor file"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid="eq-import-dialog">
        <DialogHeader>
          <DialogTitle>Impor data {company?.name}</DialogTitle>
          <DialogDescription>Gunakan format template: kolom Jenis, Kelompok, Akun, Nominal, Kategori Fiskal. Sheet "Laba Rugi" dan "Neraca" dibaca semua.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="sheet">
          <TabsList className="bg-[hsl(220_12%_8%)] border border-border">
            <TabsTrigger value="sheet" data-testid="eq-import-tab-sheet"><Link2 className="h-4 w-4 mr-1.5" /> Google Sheet</TabsTrigger>
            <TabsTrigger value="file" data-testid="eq-import-tab-file"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel / CSV</TabsTrigger>
          </TabsList>
          <TabsContent value="sheet" className="space-y-3 mt-4">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…/edit" className="h-10 bg-[hsl(220_12%_8%)]" data-testid="eq-import-sheet-url" />
            <InfoNote>Bagikan sheet terlebih dulu: Share → "Siapa saja yang memiliki link" → Viewer.</InfoNote>
            <Button onClick={fromSheet} disabled={busy || url.length < 10} className="press w-full" data-testid="eq-import-sheet-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />} Impor dari Google Sheet
            </Button>
          </TabsContent>
          <TabsContent value="file" className="space-y-3 mt-4">
            <Input type="file" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-10 bg-[hsl(220_12%_8%)]" data-testid="eq-import-file-input" />
            <Button onClick={fromFile} disabled={busy || !file} className="press w-full" data-testid="eq-import-file-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Upload & impor
            </Button>
          </TabsContent>
        </Tabs>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5">
          <div className="text-sm">Ganti semua data lama</div>
          <Switch checked={replace} onCheckedChange={setReplace} data-testid="eq-import-replace" />
        </div>
        <Button variant="link" className="text-primary px-0 h-auto w-fit" onClick={downloadTemplate} data-testid="eq-import-template-link">
          Unduh template Excel
        </Button>
      </DialogContent>
    </Dialog>
  );
}
